import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { cachePersistor } from 'graphql/client';
import { GET_USER_DETAILS } from 'graphql/queries/user';
import { User } from 'types/companies.types';
import { LoginForm } from 'types/types.types';

const useAuth = () => {
  const navigate = useNavigate();
  const client = useApolloClient();

  const [fetchUser] = useLazyQuery<{
    businessUser: User;
  }>(GET_USER_DETAILS, {});

  const onLogin = async ({ email, password }: LoginForm) => {
    try {
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password);
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Login went wrong');
      }
      const { data, error } = await fetchUser();
      if (!data?.businessUser || error) {
        throw new Error('Unauthorized access');
      }
      if (data.businessUser.role !== 'SUPER_ADMIN') {
        throw new Error(
          'Your account does not have access to this application'
        );
      }
      navigate('/');
    } catch (error: any) {
      console.error(error.code || error.message);
      toast(error.code || error.message);
    }
  };

  const onLogout = async () => {
    try {
      const auth = getAuth();
      auth.signOut();
      await client.clearStore();
      cachePersistor.purge();
    } catch (error) {
      console.error(error);
      toast('Logout failed');
    } finally {
      navigate('/login');
    }
  };

  return { onLogin, onLogout };
};

export default useAuth;
